import { X } from 'lucide-react';
import { formatDateTime, formatShortId } from '../format';
import type { BackofficeCommunicationResponse } from '../types';

export interface UserCommunicationDetailModalProps {
  communication: BackofficeCommunicationResponse;
  channel: 'email' | 'in-app';
  onClose: () => void;
}

function getPayloadString(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' ? value : null;
}

function renderMetadata(communication: BackofficeCommunicationResponse) {
  const rows = [
    ['Message ID', communication.id],
    ['Template', communication.templateKey],
    ['Status', communication.status],
    ['Created', formatDateTime(communication.createdAt)],
    ['Sent', formatDateTime(communication.sentAt)],
    ['Correlation ID', communication.correlationId ?? 'Not available'],
    ['Dedupe key', communication.dedupeKey ?? 'Not available'],
  ];

  return (
    <dl className="grid gap-3 border-b border-neutral-200 pb-4 sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-small font-semibold text-neutral-500">{label}</dt>
          <dd className="mt-1 break-words text-body text-neutral-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function UserCommunicationDetailModal({
  communication,
  channel,
  onClose,
}: UserCommunicationDetailModalProps) {
  const title =
    channel === 'email'
      ? (communication.email?.subjectRendered ?? `Email ${formatShortId(communication.id)}`)
      : (getPayloadString(communication.inApp?.displayPayload ?? {}, 'title') ??
        `In-app message ${formatShortId(communication.id)}`);
  const emailHtml = communication.email?.htmlRendered ?? null;
  const emailText = communication.email?.textRendered ?? null;
  const inAppPayload = communication.inApp?.displayPayload ?? {};
  const inAppBody = getPayloadString(inAppPayload, 'body');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 px-4 py-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-communication-detail-title"
        className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-5 py-4">
          <div className="min-w-0">
            <h2
              id="user-communication-detail-title"
              className="break-words font-inter-tight text-2xl font-semibold text-neutral-900"
            >
              {title}
            </h2>
            <p className="mt-1 text-small text-neutral-500">
              Read-only communication preview and delivery metadata
            </p>
          </div>
          <button
            type="button"
            className="focus-ring inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100"
            aria-label="Close communication details"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="flex flex-col gap-5">
            {renderMetadata(communication)}

            {channel === 'email' ? (
              <div className="flex flex-col gap-3">
                <div>
                  <h3 className="text-body font-semibold text-neutral-900">Email content</h3>
                  <p className="text-small text-neutral-500">
                    To {communication.email?.toEmail ?? 'Not available'}
                  </p>
                </div>
                {emailHtml ? (
                  <iframe
                    title="Rendered email preview"
                    sandbox=""
                    srcDoc={emailHtml}
                    className="h-[520px] w-full rounded-lg border border-neutral-200 bg-white"
                  />
                ) : (
                  <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-body text-neutral-800">
                    {emailText ?? 'No rendered email content was stored.'}
                  </pre>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <h3 className="text-body font-semibold text-neutral-900">Message content</h3>
                <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                  <p className="text-body text-neutral-900">
                    {inAppBody ?? 'No body text stored.'}
                  </p>
                </div>
                <pre className="max-h-80 overflow-auto rounded-lg border border-neutral-200 bg-white p-4 text-small text-neutral-700">
                  {JSON.stringify(inAppPayload, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
